/** Quick comprehension check shown after the safety video: every answer must be right to unlock CONFIRM. */
import { useEffect, useState } from "react";
import { Loader2, ListChecks } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function SafetyQuizPanel({ slotKey, onPassed, onFailed }) {
  const [qs, setQs] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => { setAnswers({}); setQs(null); api.get("/safety/quiz").then(({ data }) => setQs(data.questions || [])).catch(() => setQs([])); };
  useEffect(load, []);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/safety/quiz/grade", { answers, slot_key: slotKey, tz_offset_min: new Date().getTimezoneOffset() });
      if (data.passed) { toast.success(`All ${data.total} answers correct`); onPassed(data); }
      else { toast.error(`${data.correct} of ${data.total} correct — the video will restart. Watch again and retry.`); onFailed(data); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not check your answers"); }
    finally { setBusy(false); }
  };

  if (qs === null) return <div className="flex items-center gap-2 py-6 text-sm text-white/60" data-testid="safety-quiz-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading questions…</div>;
  if (!qs.length) { onPassed({ skipped: true }); return null; }
  const done = qs.every((q) => answers[q.id] !== undefined);
  return (
    <div className="space-y-4" data-testid="safety-quiz">
      <div className="flex items-center gap-2 text-sm font-bold"><ListChecks className="h-4 w-4 text-emerald-400" /> Quick check — answer all {qs.length} to confirm</div>
      {qs.map((q, i) => (
        <fieldset key={q.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid={`safety-quiz-q-${i}`}>
          <legend className="px-1 text-sm font-semibold">{i + 1}. {q.text}</legend>
          <div className="mt-1 grid gap-1.5">
            {q.options.map((o, j) => (
              <label key={j} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${answers[q.id] === j ? "border-emerald-400/60 bg-emerald-500/15" : "border-white/10 hover:bg-white/5"}`}>
                <input type="radio" name={q.id} checked={answers[q.id] === j} onChange={() => setAnswers((a) => ({ ...a, [q.id]: j }))} className="accent-emerald-500" data-testid={`safety-quiz-q-${i}-opt-${j}`} />
                {o}
              </label>
            ))}
          </div>
        </fieldset>
      ))}
      <button onClick={submit} disabled={!done || busy} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-sm font-bold uppercase tracking-widest text-white hover:bg-emerald-500 disabled:opacity-50" data-testid="safety-quiz-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListChecks className="h-4 w-4" />} Submit answers
      </button>
    </div>
  );
}
